import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import {
  AiFillInfoCircle,
  AiFillWarning,
  AiFillCheckCircle,
  AiOutlineCloseCircle,
} from 'react-icons/ai';
import { GradientBorder } from './GradientBorder';

export type AlertType = 'default' | 'warning' | 'success';

const alertTypeToColor = (alertType: AlertType) => {
  switch (alertType) {
    case 'default':
      return 'bg-blue-900';
    case 'warning':
      return 'bg-yellow-700';
    case 'success':
      return 'bg-green-700';
    default: {
      throw new Error(`Invalid alert type ${alertType}.`);
    }
  }
};

const alertTypeToIcon = (alertType: AlertType) => {
  switch (alertType) {
    case 'default':
      return <AiFillInfoCircle className="text-base" />;
    case 'warning':
      return <AiFillWarning className="text-base" />;
    case 'success':
      return <AiFillCheckCircle className="text-base" />;
    default: {
      throw new Error(`Invalid alert type ${alertType}.`);
    }
  }
};

type Props = {
  type?: AlertType;
  className?: string;
  children?: React.ReactNode;
  onDismiss?: () => void;
  autoDismissDelayMs?: number;
};

export const Alert: React.FC<Props> = ({
  type = 'default',
  className,
  children,
  onDismiss,
  autoDismissDelayMs,
  ...props
}) => {
  useEffect(() => {
    if (!onDismiss || !autoDismissDelayMs) {
      return;
    }

    const timeout = setTimeout(onDismiss, autoDismissDelayMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [onDismiss, autoDismissDelayMs]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`${className ?? ''} `}
        {...props}
      >
        <GradientBorder fill type={type}>
          <div className="px-4 py-2 font-bold uppercase">
            {onDismiss ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-4">
                  {alertTypeToIcon(type)}
                  <div className="text-xs">{children}</div>
                </div>
                <button>
                  <AiOutlineCloseCircle onClick={onDismiss} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-x-4">
                <div>{alertTypeToIcon(type)}</div>
                <div className="text-xs">{children}</div>
              </div>
            )}
          </div>
        </GradientBorder>
      </motion.div>
    </AnimatePresence>
  );
};
